import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Globe, AlertTriangle, Plug, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function SettingsDialog({ open, onOpenChange, settings, onSaved }) {
  const [mode, setMode] = useState("test");
  const [prodUrl, setProdUrl] = useState("");
  const [busy, setBusy] = useState(false);

  // GoHighLevel config
  const [ghl, setGhl] = useState({ enabled: false, location_id: "", custom_field_key: "paygate_temp_password", configured: false });
  const [ghlToken, setGhlToken] = useState("");
  const [ghlBusy, setGhlBusy] = useState(false);
  const [ghlTesting, setGhlTesting] = useState(false);

  useEffect(() => {
    if (open && settings) {
      setMode(settings.mode || "test");
      setProdUrl(settings.production_base_url || "");
    }
    if (open) {
      api.get("/settings/ghl").then((r) => { setGhl(r.data); setGhlToken(""); }).catch(() => {});
    }
  }, [open, settings]);

  const isProd = mode === "production";
  const base = isProd ? (prodUrl.replace(/\/$/, "") || "https://your-domain.com") : settings?.test_base_url;
  const preview = `${base}/api/checkout/{checkout_key}`;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", { mode, production_base_url: prodUrl });
      toast.success(`Environment set to ${data.mode.toUpperCase()}`);
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveGhl = async () => {
    setGhlBusy(true);
    try {
      const payload = {
        enabled: ghl.enabled,
        location_id: ghl.location_id,
        custom_field_key: ghl.custom_field_key || "paygate_temp_password",
      };
      if (ghlToken.trim()) payload.private_token = ghlToken.trim();
      const { data } = await api.put("/settings/ghl", payload);
      setGhl(data);
      setGhlToken("");
      toast.success("GoHighLevel settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGhlBusy(false);
    }
  };

  const testGhl = async () => {
    setGhlTesting(true);
    try {
      const { data } = await api.post("/settings/ghl/test");
      toast.success(data.message || "GoHighLevel connection successful");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGhlTesting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Globe className="h-5 w-5 text-primary" /> Endpoint Environment</DialogTitle>
          <DialogDescription>Controls the base URL used in every generated checkout endpoint you hand to your front-end.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <div className="text-sm font-medium">Production mode</div>
              <div className="text-xs text-muted-foreground">Off = Test (preview URL). On = your live domain.</div>
            </div>
            <Switch data-testid="settings-prod-switch" checked={isProd} onCheckedChange={(v) => setMode(v ? "production" : "test")} />
          </div>

          {isProd && (
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Production Base URL</Label>
              <Input data-testid="settings-prod-url-input" value={prodUrl} onChange={(e) => setProdUrl(e.target.value)} placeholder="https://paygate.revodigitaltx.com" className="font-mono text-sm" />
              <p className="text-[11px] text-muted-foreground">Must start with https:// — no trailing slash needed.</p>
            </div>
          )}

          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5">Endpoints will look like</div>
            <div className="rounded-md border border-emerald-500/30 bg-black/60 p-3 overflow-x-auto">
              <code data-testid="settings-preview" className="font-mono text-xs text-emerald-400 whitespace-nowrap">{preview}</code>
            </div>
          </div>

          {isProd && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-400">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              Make sure your live domain actually routes to this backend before sending production endpoints to customers.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button data-testid="settings-save-btn" onClick={save} disabled={busy}>
            {busy ? "Saving…" : `Save (${isProd ? "Production" : "Test"})`}
          </Button>
        </DialogFooter>

        <div className="mt-2 border-t border-border pt-5 space-y-4" data-testid="ghl-settings-section">
          <div className="flex items-center gap-2">
            <Plug className="h-5 w-5 text-primary" />
            <div>
              <div className="font-heading text-sm font-semibold">GoHighLevel (password delivery)</div>
              <div className="text-xs text-muted-foreground">
                Used to deliver forgot-password temp passwords to owners via a GHL contact field + workflow.
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <div className="text-sm font-medium flex items-center gap-2">
                Enable GHL delivery
                {ghl.configured && <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
              </div>
              <div className="text-xs text-muted-foreground">
                {ghl.configured ? "A private token is saved." : "No token saved yet."}
              </div>
            </div>
            <Switch data-testid="ghl-enabled-switch" checked={ghl.enabled} onCheckedChange={(v) => setGhl((p) => ({ ...p, enabled: v }))} />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Private Integration Token</Label>
            <Input
              data-testid="ghl-token-input"
              type="password"
              value={ghlToken}
              onChange={(e) => setGhlToken(e.target.value)}
              placeholder={ghl.configured ? "•••••••• (leave blank to keep current)" : "pit-xxxxxxxx…"}
              className="font-mono text-sm"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Location ID</Label>
              <Input
                data-testid="ghl-location-input"
                value={ghl.location_id}
                onChange={(e) => setGhl((p) => ({ ...p, location_id: e.target.value }))}
                placeholder="Location (sub-account) ID"
                className="font-mono text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Custom Field Key</Label>
              <Input
                data-testid="ghl-field-key-input"
                value={ghl.custom_field_key}
                onChange={(e) => setGhl((p) => ({ ...p, custom_field_key: e.target.value }))}
                placeholder="paygate_temp_password"
                className="font-mono text-sm"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button data-testid="ghl-save-btn" onClick={saveGhl} disabled={ghlBusy} className="flex-1">
              {ghlBusy ? "Saving…" : "Save GHL settings"}
            </Button>
            <Button data-testid="ghl-test-btn" variant="outline" onClick={testGhl} disabled={ghlTesting || !ghl.configured}>
              {ghlTesting ? "Testing…" : "Test connection"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
